import type { AssignedUser } from "./types.js";

/**
 * SB6-II-05 (Track 4) — render an assignee chip label that NEVER shows literal
 * "null null". Names can be null on the wire (cross-tenant users with sparse
 * directory data; DB-MIG-02 + the remigration populate them, but the render
 * must degrade gracefully if a name is ever still missing).
 *
 * TNCY-6 §e / XT-D9 — when a granted tenant's grant withholds names, the BE
 * sends null first/last (+ optionally `namesHidden`/`placeholder`). We surface
 * a TENANT-SCOPED placeholder ("CopterArt user") rather than a bare "(Tenant)"
 * so the chip reads as a real, assignable person without leaking their name.
 *
 *  - first + last + tenant       → "First Last (Tenant)"
 *  - name only                   → "First Last"
 *  - names withheld + placeholder → the BE placeholder verbatim
 *  - names withheld + tenant      → "{Tenant} user"   (synthesised)
 *  - tenant only                 → "{Tenant} user"
 *  - nothing                     → "Unknown user"
 */
export function userLabel(u: AssignedUser): string {
  const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  if (name && u.tenantName) return `${name} (${u.tenantName})`;
  if (name) return name;
  // Name withheld / missing — prefer the BE-sent placeholder; else synthesise
  // a tenant-scoped one. Both keep the user assignable by id.
  const placeholder = u.placeholder?.trim();
  if (placeholder) return placeholder;
  if (u.tenantName) return `${u.tenantName} user`;
  return "Unknown user";
}
